import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import Snackbar from "@material-ui/core/Snackbar";
import { FMSReportAction } from "../../actions/FMSReportAction";

// ─────────────────────────────────────────────────────────────────────────────
// Icons
// ─────────────────────────────────────────────────────────────────────────────
const Svg = ({ size = 16, children }) => (
  <svg
    viewBox="0 0 24 24" width={size} height={size} fill="none"
    stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
  >
    {children}
  </svg>
);

const CheckIcon = ({ size }) => (
  <Svg size={size}><polyline points="20 6 9 17 4 12" /></Svg>
);
const CrossIcon = ({ size }) => (
  <Svg size={size}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>
);
const ResetIcon = ({ size }) => (
  <Svg size={size}>
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
  </Svg>
);
const TruckIcon = ({ size }) => (
  <Svg size={size}>
    <rect x="1" y="3" width="15" height="13" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </Svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// Colour system
// ─────────────────────────────────────────────────────────────────────────────
export const REVIEW_THEME = {
  approve: {
    color: "#15803d", soft: "#dcfce7", border: "#86efac", halo: "#f0fdf4",
    Icon: CheckIcon, title: "Approve Event?", subtitle: "CONFIRM APPROVAL",
    confirm: "Yes, Approve", verb: "Approve",
    desc: "This marks the event as genuine. The status changes to Approved and can only be changed after a reset.",
  },
  reject: {
    color: "#b91c1c", soft: "#fee2e2", border: "#fca5a5", halo: "#fef2f2",
    Icon: CrossIcon, title: "Reject Event?", subtitle: "CONFIRM REJECTION",
    confirm: "Yes, Reject", verb: "Reject",
    desc: "This marks the event as not genuine (for example a sensor spike). It can only be changed after a reset.",
  },
  reset: {
    color: "#d97706", soft: "#fef3c7", border: "#fcd34d", halo: "#fffbeb",
    Icon: ResetIcon, title: "Reset Review?", subtitle: "REVIEW WILL BE CLEARED",
    confirm: "Yes, Reset", verb: "Reset",
    desc: "The current review is cleared and the event goes back to Pending so it can be approved or rejected again.",
  },
  review: {
    color: "#1d4ed8", soft: "#dbeafe", border: "#93c5fd", halo: "#eff6ff",
  },
};

const QUICK_REMARKS = {
  approve: ["Verified with driver", "Verified with fuel slip"],
  reject: ["Sensor spike", "Tank sloshing", "Calibration issue", "Duplicate event"],
};

const STATUS_VIEW = {
  UNCHECKED: { label: "Pending", color: "#1d4ed8", bg: "#dbeafe" },
  APPROVED: { label: "Approved", color: "#15803d", bg: "#dcfce7" },
  NOTAPPROVED: { label: "Rejected", color: "#b91c1c", bg: "#fee2e2" },
};

// ─────────────────────────────────────────────────────────────────────────────
// Small building blocks
// ─────────────────────────────────────────────────────────────────────────────
export const ReviewStatusPill = ({ status }) => {
  const s = STATUS_VIEW[status] || STATUS_VIEW.UNCHECKED;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 12px",
        borderRadius: 999, fontSize: 12, fontWeight: 700, background: s.bg, color: s.color,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.color }} />
      {s.label}
    </span>
  );
};

const ActionBtn = ({ type, label, onClick, compact = true }) => {
  const t = REVIEW_THEME[type];
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        height: compact ? 28 : 30, padding: compact ? "0 10px" : "0 14px",
        borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: "inherit",
        cursor: "pointer", transition: "all 0.2s ease",
        border: `1px solid ${hover ? t.color : t.border}`,
        background: hover ? t.color : t.soft,
        color: hover ? "#ffffff" : t.color,
        boxShadow: hover ? `0 4px 12px ${t.color}55` : "none",
        transform: hover ? "translateY(-1px)" : "none",
      }}
    >
      <t.Icon size={13} />
      {label}
    </button>
  );
};

// Pending rows get Approve + Reject; reviewed rows get Reset only.
export const ReviewActionButtons = ({ row, onAction }) => (
  <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
    {row.reviewStatus === "UNCHECKED" ? (
      <>
        <ActionBtn type="approve" label="Approve" onClick={() => onAction("approve")} />
        <ActionBtn type="reject" label="Reject" onClick={() => onAction("reject")} />
      </>
    ) : (
      <ActionBtn type="reset" label="Reset" onClick={() => onAction("reset")} />
    )}
  </div>
);

const VehicleChip = ({ no, tone }) => (
  <span
    style={{
      display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px",
      borderRadius: 6, fontSize: 12, fontWeight: 700,
      background: tone.halo, color: tone.color, border: `1px solid ${tone.border}`,
    }}
  >
    <TruckIcon size={13} />
    {no}
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// options: onDone(), eventLabel ("Fuel Fill" | "Fuel Drop"), qtyKey ("fill" | "droppage")
// ─────────────────────────────────────────────────────────────────────────────
export function useFuelEventReview({ onDone, eventLabel = "Fuel", qtyKey }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [dialog, setDialog] = useState({ open: false, action: null, rows: [], bulk: false });
  const [remarks, setRemarks] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const toggleOne = (id) =>
    setSelectedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // only pending rows are selectable
  const togglePage = (rows) => {
    const ids = rows.filter((r) => r.reviewStatus === "UNCHECKED").map((r) => r._id).filter(Boolean);
    setSelectedIds((p) =>
      ids.length && ids.every((id) => p.includes(id))
        ? p.filter((id) => !ids.includes(id))
        : Array.from(new Set([...p, ...ids])),
    );
  };

  const clearSelection = () => setSelectedIds([]);

  // rows = array of row objects (needed to show vehicle / qty in the dialog)
  const openDialog = (action, rows, bulk = false) => {
    if (!rows.length) return;
    setRemarks("");
    setConfirmed(false);
    setDialog({ open: true, action, rows, bulk });
  };
  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  const submit = async () => {
    const { action, rows, bulk } = dialog;
    const ids = rows.map((r) => r._id);
    setSaving(true);
    const res = bulk
      ? await FMSReportAction.reviewFmsFuelEventsBulk(ids, action, remarks.trim())
      : await FMSReportAction.reviewFmsFuelEvent(ids[0], action, remarks.trim());
    setSaving(false);

    if (!res) {
      setToast("Action failed. Please try again.");
      return;
    }
    const skipped = res.skipped?.length || 0;
    setToast(`${res.updated} of ${res.requested} updated${skipped ? `, ${skipped} skipped` : ""}`);
    closeDialog();
    clearSelection();
    onDone && onDone();
  };

  return {
    selectedIds, toggleOne, togglePage, clearSelection,
    dialog, openDialog, closeDialog,
    remarks, setRemarks, confirmed, setConfirmed,
    saving, submit, toast, setToast, eventLabel, qtyKey,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Bulk bar (review = blue)
// ─────────────────────────────────────────────────────────────────────────────
export const BulkReviewBar = ({ review, rows }) => {
  const { selectedIds, togglePage, openDialog } = review;
  const rv = REVIEW_THEME.review;
  const pending = rows.filter((r) => r.reviewStatus === "UNCHECKED");
  const allSelected = pending.length > 0 && pending.every((r) => selectedIds.includes(r._id));
  const selectedRows = rows.filter((r) => selectedIds.includes(r._id));

  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "4px 14px", marginBottom: 10, borderRadius: 10,
        background: rv.halo, border: `1px solid ${rv.border}`
      }}
    >
      <label
        style={{
          display: "flex", alignItems: "center", gap: 8, fontSize: 13,
          fontWeight: 600, color: "#1e293b", cursor: "pointer",marginTop:5
        }}
      >
        <input
          type="checkbox"
          disabled={pending.length === 0}
          checked={allSelected}
          onChange={() => togglePage(rows)}
        />
        Select all pending ({pending.length})
      </label>

      <span
        style={{
          padding: "3px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700,
          background: rv.soft, color: rv.color,
        }}
      >
        {selectedIds.length} selected
      </span>

      {selectedIds.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
          <ActionBtn
            type="approve" compact={false} label={`Approve ${selectedIds.length}`}
            onClick={() => openDialog("approve", selectedRows, true)}
          />
          <ActionBtn
            type="reject" compact={false} label={`Reject ${selectedIds.length}`}
            onClick={() => openDialog("reject", selectedRows, true)}
          />
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Dialog
// ─────────────────────────────────────────────────────────────────────────────
export const ReviewDialogs = ({ review }) => {
  const {
    dialog, remarks, setRemarks, confirmed, setConfirmed, saving,
    closeDialog, submit, toast, setToast, eventLabel, qtyKey,
  } = review;

  const { action, rows, bulk } = dialog;
  const t = REVIEW_THEME[action || "approve"];
  const rv = REVIEW_THEME.review;
  const n = rows.length;
  const row = rows[0] || {};
  const canSubmit = !saving && (!bulk || confirmed);

  const title = bulk ? `${t.verb} ${n} Events?` : t.title;
  const confirmLabel = saving ? "Saving..." : bulk ? `${t.verb} ${n} Events` : t.confirm;

  const cardStyle = {
    background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10,
    padding: "12px 14px", marginBottom: 12,
  };
  const smallLabel = {
    fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: "#94a3b8", marginBottom: 4,
  };

  return (
    <>
      <Dialog
        open={dialog.open}
        onClose={saving ? undefined : closeDialog}
        PaperProps={{
          style: {
            width: 440, maxWidth: "92vw", borderRadius: 6, overflow: "hidden",
            borderTop: `4px solid ${t.color}`,
          },
        }}
      >
        <div style={{ padding: "22px 24px 20px", textAlign: "center" }}>
          {/* icon with halo */}
          <div
            style={{
              width: 76, height: 76, borderRadius: "50%", background: t.halo,
              margin: "0 auto 10px", display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <div
              style={{
                width: 56, height: 56, borderRadius: "50%", background: t.soft,
                border: `2px solid ${t.border}`, color: t.color,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <t.Icon size={26} />
            </div>
          </div>

          <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>{title}</div>

          {bulk ? (
            <div
              style={{
                display: "inline-block", marginTop: 6, padding: "2px 12px", borderRadius: 999,
                fontSize: 10, fontWeight: 800, letterSpacing: 0.8,
                background: rv.soft, color: rv.color,
              }}
            >
              BULK REVIEW
            </div>
          ) : (
            <div style={{ marginTop: 4, fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: t.color }}>
              {t.subtitle}
            </div>
          )}

          <div style={{ margin: "12px 0 16px", fontSize: 13, lineHeight: 1.5, color: "#64748b" }}>
            {t.desc}
          </div>

          {/* info card */}
          <div style={{ ...cardStyle, textAlign: "left" }}>
            {bulk ? (
              <>
                <div style={smallLabel}>{n} EVENTS SELECTED</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {rows.slice(0, 4).map((r) => (
                    <VehicleChip key={r._id} no={r.vehicleNo} tone={t} />
                  ))}
                  {n > 4 && (
                    <span style={{ alignSelf: "center", fontSize: 12, fontWeight: 700, color: "#64748b" }}>
                      +{n - 4} more
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <VehicleChip no={row.vehicleNo} tone={t} />
                  <div style={{ textAlign: "right" }}>
                    <div style={smallLabel}>{eventLabel.toUpperCase()}</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: t.color }}>
                      {row[qtyKey]} L
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
                  {row.dateTime} · {row.area}
                </div>
                {action === "reset" && (
                  <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Current status</span>
                    <ReviewStatusPill status={row.reviewStatus} />
                    {row.reviewRemarks && (
                      <span style={{ fontSize: 12, color: "#64748b" }}>“{row.reviewRemarks}”</span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* remarks (approve / reject only) */}
          {action !== "reset" && (
            <div style={{ textAlign: "left", marginBottom: 12 }}>
              <div style={smallLabel}>REMARKS (OPTIONAL)</div>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                placeholder="Add a note for this review..."
                style={{
                  width: "100%", boxSizing: "border-box", resize: "none", padding: "10px 12px",
                  borderRadius: 8, border: `1px solid ${t.border}`, outline: "none",
                  fontFamily: "inherit", fontSize: 13, color: "#0f172a",
                }}
              />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                {(QUICK_REMARKS[action] || []).map((q) => (
                  <button
                    key={q} type="button" onClick={() => setRemarks(q)}
                    style={{
                      padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                      cursor: "pointer", fontFamily: "inherit",
                      background: remarks === q ? t.color : t.halo,
                      color: remarks === q ? "#fff" : t.color,
                      border: `1px solid ${t.border}`,
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* bulk confirmation checkbox */}
          {bulk && (
            <label
              style={{
                display: "flex", alignItems: "center", gap: 10, textAlign: "left",
                padding: "11px 14px", marginBottom: 12, borderRadius: 10, cursor: "pointer",
                border: "1px solid #e2e8f0", fontSize: 13, fontWeight: 700, color: "#1e293b",
              }}
            >
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              I have checked these {n} events
            </label>
          )}

          {/* footer */}
          <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
            <button
              type="button" onClick={closeDialog} disabled={saving}
              style={{
                flex: 1, height: 44, borderRadius: 8, background: "#fff",
                border: "1px solid #e2e8f0", color: "#64748b", fontSize: 14,
                fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Cancel
            </button>
            <button
              type="button" onClick={submit} disabled={!canSubmit}
              style={{
                flex: 1, height: 44, borderRadius: 8, border: "none", background: t.color,
                color: "#fff", fontSize: 14, fontWeight: 700, fontFamily: "inherit",
                cursor: canSubmit ? "pointer" : "not-allowed",
                opacity: canSubmit ? 1 : 0.5,
                boxShadow: canSubmit ? `0 6px 16px ${t.color}55` : "none",
                transition: "all 0.2s ease",
              }}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </Dialog>

      <Snackbar
        open={!!toast} autoHideDuration={4000} onClose={() => setToast("")} message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};